import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { EmptyState } from "@/components/storefront/common";
import { toast } from "sonner";
import { MapPin, Trash2, Plus, X } from "lucide-react";

const EMPTY = { fullName: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", isDefault: false };

export default function AccountAddresses() {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/addresses").then(({ data }) => setAddresses(data.addresses));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/addresses", form);
      toast.success("Address added");
      setShowForm(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { await api.delete(`/addresses/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-900 text-lg">Saved Addresses</h2>
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600" data-testid="add-address-btn"><Plus className="h-4 w-4" /> Add New</button>
      </div>

      {addresses.length === 0 && !showForm ? (
        <EmptyState icon={MapPin} title="No addresses yet" text="Add a delivery address for faster checkout.">
          <button onClick={() => setShowForm(true)} className="bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-lg">Add Address</button>
        </EmptyState>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`address-${a.id}`}>
              <div className="flex items-start justify-between">
                <p className="font-semibold text-slate-900 text-sm">{a.fullName} {a.isDefault && <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded ml-1">Default</span>}</p>
                <button onClick={() => remove(a.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <p className="text-sm text-slate-600 mt-1">{a.line1}, {a.line2 && `${a.line2}, `}{a.city}, {a.state} — {a.pincode}</p>
              <p className="text-sm text-slate-500 mt-0.5 font-mono">{a.phone}</p>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setShowForm(false)} />
          <form onSubmit={save} className="relative bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="address-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Add Address</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["fullName", "Full Name"], ["phone", "Phone"], ["line1", "Address Line 1", true], ["line2", "Address Line 2", true], ["city", "City"], ["state", "State"], ["pincode", "Pincode"], ["landmark", "Landmark"]].map(([k, label, full]) => (
                <div key={k} className={full ? "sm:col-span-2" : ""}>
                  <label className="text-xs font-medium text-slate-600">{label}</label>
                  <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`addr-${k}`}
                    className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-slate-600">
              <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))} className="accent-amber-500 h-4 w-4" /> Set as default
            </label>
            <button className="w-full mt-4 bg-slate-900 text-white font-semibold py-3 rounded-lg" data-testid="save-address-btn">Save Address</button>
          </form>
        </div>
      )}
    </div>
  );
}
